import { Ref, useCallback, useMemo } from "react";
import { ActionMeta, GroupBase, MultiValue, PropsValue, SelectInstance, SingleValue } from "react-select";
import { genericMemo } from "../../../../utils";
import { Select, SelectProps } from "../Select";

export type SelectOptionType<T> = {
  value: T,
  label: string,
}

export type SimpleSelectProps<T> = Omit<SelectProps<SelectOptionType<T>>, "options" | "value" | "defaultValue" | "onChange"> & {
  options?: T[],
  filterOptionsFunc?: (options: SelectOptionType<T>[]) => SelectOptionType<T>[],
  sortOptionsFunc?: (options: SelectOptionType<T>[]) => SelectOptionType<T>[],
  emptyItem?: SelectOptionType<T>,
  allowEmpty?: boolean
  setOptionLabel?: (option: T) => any,
  selectRef?: Ref<SelectInstance<SelectOptionType<T>, boolean, GroupBase<SelectOptionType<T>>>>
  value?: PropsValue<T>,
  defaultValue?: PropsValue<T>,
  onChange?: ((newValue: (SingleValue<T> | MultiValue<T>), actionMeta: ActionMeta<SelectOptionType<T>>) => void) | undefined,
}

const defaultSetOptionLabel = (option: any): string => option;

const emptyItem = {
  value: null,
  label: "Empty",
};

export const SimpleSelect = genericMemo(<T extends any>(
  {
    options,
    emptyItem,
    allowEmpty,
    value,
    onChange,
    setOptionLabel,
    filterOptionsFunc,
    sortOptionsFunc,
    defaultValue,
    ...props
  }: SimpleSelectProps<T>) => {

  const wrappedOptions = useMemo(
    (): SelectOptionType<T>[] => {
      const setLabel = setOptionLabel || defaultSetOptionLabel;
      let selectOptions = options?.map?.(option => ({
        value: option,
        label: setLabel(option),
      })) || [] as SelectOptionType<T>[];
      selectOptions = filterOptionsFunc ? filterOptionsFunc(selectOptions) : selectOptions;
      selectOptions = sortOptionsFunc ? sortOptionsFunc(selectOptions) : selectOptions;

      if (allowEmpty && emptyItem) {
        return [ emptyItem, ...selectOptions ];
      }
      return selectOptions;
    },
    [ options, setOptionLabel, emptyItem, allowEmpty, filterOptionsFunc, sortOptionsFunc ],
  );

  const selectValue = useMemo((): SelectOptionType<T> | ReadonlyArray<SelectOptionType<T>> | undefined => {
    const setLabel = setOptionLabel || defaultSetOptionLabel;
    if (value !== undefined) {
      if (Array.isArray(value)) {
        return value.map(v => wrappedOptions?.find(x => x.value === v))
          .filter(x => x !== undefined) as SelectOptionType<T>[];
      }
      return wrappedOptions?.find?.(x => x.value === value) || ({
        value,
        label: setLabel(value as T),
      } as SelectOptionType<T>);
    }
    return undefined;
  }, [ value, wrappedOptions, setOptionLabel ]);

  const wrappedDefaultValue = useMemo((): SelectOptionType<T> | ReadonlyArray<SelectOptionType<T>> | undefined => {
    const setLabel = setOptionLabel || defaultSetOptionLabel;
    if (defaultValue !== undefined) {
      if (Array.isArray(defaultValue)) {
        return defaultValue.map(v => wrappedOptions?.find(x => x.value === v))
          .filter(x => x !== undefined) as SelectOptionType<T>[];
      }
      return wrappedOptions?.find?.(x => x.value === defaultValue) || ({
        value: defaultValue,
        label: setLabel(defaultValue as T),
      } as SelectOptionType<T>);
    }
    return undefined;
  }, [ defaultValue, wrappedOptions, setOptionLabel ]);


  const selectOnChange = useCallback((v: MultiValue<SelectOptionType<T>> | SingleValue<SelectOptionType<T>>, actionMeta: ActionMeta<SelectOptionType<T>>) => {
    if (Array.isArray(v)) {
      onChange?.(v.map(x => x?.value) as any, actionMeta);
    } else {
      onChange?.((v as SelectOptionType<T>)?.value as any, actionMeta);
    }
  }, [ onChange ]);

  return (
    <Select
      isSearchable={false}
      {...props}
      useAsyncSelect={false}
      options={wrappedOptions}
      value={selectValue}
      defaultValue={wrappedDefaultValue}
      onChange={selectOnChange}
    />
  );
});

export default SimpleSelect;
